import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

const fmt = (v, currency = "INR") =>
  v == null ? "—" : new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(v));

const STATE_LABEL = {
  RECOVERABLE: "Recoverable", NON_RECOVERABLE: "Non-recoverable", REVIEW: "Review",
  RECOVERABLE_BY_THIS_ENTITY: "Recoverable by this entity",
  NOT_RECOVERABLE_BY_THIS_ENTITY: "Not recoverable by this entity",
  BENEFICIARY_OTHER_ENTITY: "Credit belongs to another entity",
  INCLUDE_IN_LANDED_COST: "Include in landed cost", EXCLUDE: "Exclude from landed cost",
};
const TAX = ["RECOVERABLE", "NON_RECOVERABLE", "REVIEW"];
const TCS = ["RECOVERABLE_BY_THIS_ENTITY", "NOT_RECOVERABLE_BY_THIS_ENTITY", "BENEFICIARY_OTHER_ENTITY", "REVIEW"];
const CHARGE = ["INCLUDE_IN_LANDED_COST", "EXCLUDE", "REVIEW"];
const statesFor = (k) => (k === "TCS" ? TCS : ["GST", "CGST", "SGST", "IGST", "GST_CESS", "VAT"].includes(k) ? TAX : CHARGE);

const Override = ({ d, busy, onSave, onCancel }) => {
  const [state, setState] = useState(d.state || "REVIEW");
  const [effect, setEffect] = useState("INCLUDE");
  const [reason, setReason] = useState("");
  const other = state === "BENEFICIARY_OTHER_ENTITY";
  return (
    <div className="mt-2 space-y-2" data-testid={`landed-override-${d.component_kind}`}>
      <div className="flex flex-wrap gap-2">
        {statesFor(d.component_kind).map((s) => (
          <button key={s} type="button" onClick={() => setState(s)} data-testid={`landed-state-${d.component_kind}-${s}`}
            className={`min-h-[38px] px-3 rounded-full border text-[13px] ${state === s ? "border-[#2f5d3a] bg-[#eef3ec] text-[#2f5d3a] font-medium" : "border-[#E4E0D8] bg-white text-archival-secondary"}`}>
            {STATE_LABEL[s]}
          </button>
        ))}
      </div>
      {other && (
        <div className="flex gap-2">
          {["INCLUDE", "EXCLUDE"].map((e) => (
            <button key={e} type="button" onClick={() => setEffect(e)} data-testid={`landed-effect-${d.component_kind}-${e}`}
              className={`min-h-[38px] px-3 rounded-md border text-[13px] ${effect === e ? "border-[#2f5d3a] bg-[#eef3ec] text-[#2f5d3a] font-medium" : "border-[#E4E0D8] bg-white text-archival-secondary"}`}>
              {e === "INCLUDE" ? "This outlet bears the cost" : "This outlet does not bear the cost"}
            </button>
          ))}
        </div>
      )}
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required, kept in the audit trail)"
        data-testid={`landed-reason-${d.component_kind}`} className="w-full h-11 px-3 rounded-md border border-[#E4E0D8] text-[14px]" />
      <div className="flex gap-2">
        <button type="button" disabled={busy || !reason.trim()} data-testid={`landed-save-${d.component_kind}`}
          onClick={() => onSave({ component_kind: d.component_kind, state, landed_cost_effect: other ? effect : null, beneficiary_type: other ? "OTHER_ENTITY" : null, reason })}
          className="h-11 px-5 rounded-md text-white text-[14px] font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--action)" }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Apply to this bill"}
        </button>
        <button type="button" onClick={onCancel} className="h-11 px-4 rounded-md border border-[#E4E0D8] text-[14px]">Cancel</button>
      </div>
    </div>
  );
};

/** Applied landed-cost policy per component + the resulting purchase cost. The printed invoice total is shown
 *  alongside and is never replaced by the landed figure. */
export const LandedCostPanel = ({ landed, currency = "INR", canEdit = false, busy = false, error = null,
                                  onOverride, testId = "landed-cost" }) => {
  const [open, setOpen] = useState(null);
  if (!landed || !(landed.decisions || []).length) return null;
  const hold = landed.review_required || [];
  return (
    <section className="rounded-md border border-[#E4E0D8] bg-white px-4 py-3" data-testid={testId}>
      <h2 className="text-[14px] font-semibold text-archival-primary">Landed cost</h2>
      <ul className="mt-2 divide-y divide-[#F0EDE6]">
        {landed.decisions.map((d) => (
          <li key={d.component_kind} className="py-2.5" data-testid={`landed-row-${d.component_kind}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[14px] text-archival-primary">{d.component_kind.replace("_", " / ")}</p>
                <p className={`text-[13px] ${d.effect === "HOLD_FOR_DECISION" ? "text-amber-800" : "text-archival-secondary"}`}
                  data-testid={`landed-effect-text-${d.component_kind}`}>
                  {d.state_label} · {d.effect_label}
                </p>
                <p className="text-[12px] text-archival-muted">
                  {d.source === "BILL_OVERRIDE" ? `Set on this bill — ${d.reason}`
                    : d.source === "NO_POLICY_RECORDED" ? "No policy recorded for this outlet"
                    : d.source === "OUTLET_POLICY" ? `Outlet policy from ${d.effective_from}` : `Organisation default from ${d.effective_from}`}
                </p>
              </div>
              {canEdit && open !== d.component_kind && (
                <button type="button" onClick={() => setOpen(d.component_kind)} data-testid={`landed-change-${d.component_kind}`}
                  className="min-h-[38px] px-3 rounded-md border border-[#E4E0D8] text-[13px] shrink-0">Change</button>
              )}
            </div>
            {open === d.component_kind && (
              <Override d={d} busy={busy} onCancel={() => setOpen(null)}
                onSave={async (b) => { await onOverride(b); setOpen(null); }} />
            )}
          </li>
        ))}
      </ul>
      {hold.length > 0 && (
        <p role="alert" className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900"
          data-testid="landed-review-required">
          <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />
          Landed cost decision required — {hold.map((h) => `${h.component_kind} ${fmt(h.amount, currency)}`).join(", ")}.
          Inventory will not receive this purchase cost until it is decided.
        </p>
      )}
      {error && <p role="alert" className="mt-2 text-[13px] text-red-800" data-testid="landed-error">{error}</p>}
      <dl className="mt-3 border-t border-[#E4E0D8] pt-2 space-y-1">
        <div className="flex justify-between text-[13px]"><dt className="text-archival-secondary">Merchandise</dt><dd className="tabular-nums" data-testid="landed-merchandise">{fmt(landed.merchandise_total, currency)}</dd></div>
        <div className="flex justify-between text-[13px]"><dt className="text-archival-secondary">Included components</dt><dd className="tabular-nums" data-testid="landed-included">{fmt(landed.included_components_total, currency)}</dd></div>
        <div className="flex justify-between text-[15px] font-semibold text-archival-primary"><dt>Landed purchase cost</dt><dd className="tabular-nums" data-testid="landed-total">{fmt(landed.landed_total, currency)}</dd></div>
        <div className="flex justify-between text-[12px] text-archival-muted"><dt>Invoice total (unchanged)</dt><dd className="tabular-nums" data-testid="landed-invoice-total">{fmt(landed.invoice_total, currency)}</dd></div>
      </dl>
      {landed.historical_note && <p className="mt-2 text-[12px] text-archival-muted" data-testid="landed-historical-note">{landed.historical_note}</p>}
    </section>
  );
};
